<template>
  <modal
    v-if="show"
    height="672px"
    width="920px"
    bodyOverflow="auto"
    :title="mode === 'offers' ? t('Offer import') : t('Landing import')"
    @close="checkClose"
    :read-article-url="articleUrl"
  >
    <template #default>
      <div class="mb-16">{{ subtitle }}</div>

      <ui-select
        v-model="workspaceId"
        :items="store.user.workspaces"
        :label="t('Workspace')"
        item-text="name"
        item-value="id"
        :return-object="false"
        attach
        :hide-details="2"
      ></ui-select>

      <ui-textarea
        v-model="importText"
        :placeholder="placeholder"
        rows="20"
      ></ui-textarea>
    </template>

    <template #footer>
      <section class="justify-space-between xs12" style="display: flex; width: 100%">
        <div class="d-flex align-items-center">
          <error-aggregator
            v-model="errors"
            v-model:active="errorsControlActive"
          ></error-aggregator>
        </div>
        <div class="d-flex justify-end">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          ></ui-button>

          <ui-button
            :text="t('Import')"
            button-style="accent"
            icon="far fa-file-import"
            @click="importData"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import md5 from 'md5';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import { postLandingsImportApiRequest } from '@/services/api/landings-api';
import { postOffersImportApiRequest } from '@/services/api/offers-api';
import { useStore } from '@/store';
import {
  parseFlowErrorsRaw,
  translateFlowErrorsRaw,
} from '@/utils/parseApiErrors';

const props = withDefaults(
  defineProps<{
    data?: { mode?: string };
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const { t, locale } = useI18n();
const store = useStore();

const show = ref(false);
const mode = ref('offers');
const workspaceId = ref('');
const importText = ref('');
const oldHash = ref('');
const errorsRaw = ref<unknown[][] | null>(null);
const errorMessage = ref('');
const errorsControlActive = ref(false);

const importApiRequest = computed(() =>
  mode.value === 'offers' ? postOffersImportApiRequest : postLandingsImportApiRequest,
);

const subtitle = computed(() =>
  mode.value === 'offers'
    ? t("name{'|'}url{'|'}payout(auto{'|'}manual){'|'}currency(usd,gbp etc.)[{'|'}geo(us,gb etc.){'|'}aff. network] [] - optional.")
    : t("name{'|'}url [{'|'}geo(us,gb etc.){'|'}offers] [] - optional."),
);

const placeholder = computed(() =>
  mode.value === 'offers'
    ? t("sample offer name{'|'}https://testoffer.com{'|'}auto{'|'}usd{'|'}us{'|'}maxbounty")
    : t("sample landing page{'|'}https://yourlandingpage.com{'|'}gb{'|'}1"),
);

const articleUrl = computed(() =>
  mode.value === 'offers'
    ? `https://docs.bemob.com/${locale}/offers-import`
    : `https://docs.bemob.com/${locale}/landings-import`,
);

const errors = computed(() =>
  translateFlowErrorsRaw(errorsRaw.value, errorMessage.value),
);

const getCurrentHash = () =>
  md5(
    JSON.stringify({
      importText: importText.value,
      workspaceId: workspaceId.value,
    }),
  );

const close = () => {
  emit('close');
};

const checkClose = () => {
  if (oldHash.value !== getCurrentHash()) {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: { onConfirm: () => close() },
    });
  } else {
    close();
  }
};

const importData = async () => {
  store.setLoading(true);

  try {
    await importApiRequest.value({
      data: importText.value,
      workspaceId: workspaceId.value,
    });
    store.setLoading(false);
    close();
    $emitter.emit('refresh-report');
    $emitter.emit('toast-success', t('Changes have been successfully saved'));
  } catch (error) {
    store.setLoading(false);
    const responseData = (error as { response?: { data?: { errors?: unknown; message?: string } } })
      ?.response?.data;

    if (responseData?.errors) {
      errorsRaw.value = parseFlowErrorsRaw(responseData.errors);
      errorMessage.value = responseData.message || '';
      errorsControlActive.value = true;
    }
  }
};

onMounted(() => {
  mode.value = props.data.mode || 'offers';
  workspaceId.value = store.user.workspaces[0].id;
  show.value = true;
  oldHash.value = getCurrentHash();
  store.setLoading(false);
});
</script>

<style lang="scss" scoped>
.create-campaign {
  overflow-y: auto;
  height: 100%;
}
</style>
